import IterableDataProcessor from "../../entities/IterableDataProcessor.js";
import ResultData from "../../enums/ResultData.js";
import ResultFormatterBase from "../ResultFormatterBase.js";

class DateTimeFormatter extends ResultFormatterBase {
    constructor(dateParameterPath, sourceDateTimeZoneOffset, fallbackDateTimeValue) {
        if (!(dateParameterPath instanceof Array)) {
            throw new TypeError('dateParameterPath must be an array');
        }

        super();

        if (typeof(fallbackDateTimeValue) === 'undefined') {
            this.fallbackDateTimeValue = null;
        }
        else {
            this.fallbackDateTimeValue = fallbackDateTimeValue;
        }

        if (typeof(sourceDateTimeZoneOffset) === "undefined") {
            // let's assume local timezone as default
            this.sourceDateTimeZoneOffset = '';
        }
        else if(typeof(sourceDateTimeZoneOffset) === 'string') {
            this.sourceDateTimeZoneOffset = sourceDateTimeZoneOffset;
        }
        else if(typeof(sourceDateTimeZoneOffset) === 'number') {
            this.sourceDateTimeZoneOffset = DateTimeFormatter.numericOffsetToString(sourceDateTimeZoneOffset);
        }
        else {
            throw new TypeError('sourceDateTimeZoneOffset must be an number of minutes, string in +/-HH:MM format or undefined to use local time zone');
        }

        this.dateParameterPath = dateParameterPath;
    }

    static numericOffsetToString(timeZoneOffset) {
        const isNegative = timeZoneOffset < 0;
        const hours = Math.abs(
            isNegative ? Math.ceil(timeZoneOffset / 60): Math.floor(timeZoneOffset / 60)
        );
        const minutes =  Math.abs(timeZoneOffset % 60);

        return `${(isNegative ? '-': '+')}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    format(result) {
        if (!result) {
            return result;
        }

        if (!result.hasOwnProperty(ResultData.Data.key) || !result[ResultData.Data.key]) {
            return result;
        }

        try {
            const currentDate = IterableDataProcessor.getObjectContent(result, this.dateParameterPath);
            if (currentDate instanceof Date) {
                return result;
            }
            if (typeof(currentDate) === "number" && currentDate > 0) {
                // unix timestamp
                return this.formatNumericTs(result, currentDate);
            }
            if (typeof(currentDate) !== 'string' || !currentDate.trim()) {
                return this.formatUsingFallback(result);
            }

            return this.formatStringifiedDate(result, currentDate);
		}
		catch (e) {
			return this.formatUsingFallback(result);
		}
    }

    formatUsingFallback(result) {
        IterableDataProcessor.setObjectContent(result, this.dateParameterPath, this.fallbackDateTimeValue);
        return result;
    }

    formatNumericTs(result, numericTs) {
        // see http://ws.flightscope.local/schema/core/UTC_TIMESTAMP_MS.json#
        // see https://bitbucket.org/flightscope/dto-schema/src/1fc52a1a9209d94cfb1b306428dfa6a2a6177763/core/UTC_TIMESTAMP_MS.json?at=feature%2Fbaseball
        if (numericTs < 1388534400000) {
            // see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date
            numericTs *= 1000;
        }

        const parsed = new Date(numericTs);
        if (!parsed || !(parsed instanceof Date) || !isFinite(parsed)) {
            return this.formatUsingFallback(result);
        }

        IterableDataProcessor.setObjectContent(result, this.dateParameterPath, parsed);
        return result;
    }

    formatStringifiedDate(result, currentDate) {
        let dateWithTimeZoneOffset = currentDate;
        if (!/:\d{2}[+-]\d{2}:\d{2}$/.test(currentDate)) {
            // we have no time zone string
            dateWithTimeZoneOffset = currentDate + this.sourceDateTimeZoneOffset;
        }

        const parsed = new Date(dateWithTimeZoneOffset);
        if (!parsed || !(parsed instanceof Date) || !isFinite(parsed)) {
            return this.formatUsingFallback(result);
        }

        IterableDataProcessor.setObjectContent(result, this.dateParameterPath, parsed);
        return result;
    }
}

export default DateTimeFormatter;